/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/userDetails.scss';

import React, {FocusEventHandler, MouseEventHandler, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import User from '../../../../models/userModel';

import {
    CommonAdminErrors,
    Country,
    DataMode,
    SubscriptionStatus,
    UserStatus,
    UserType
} from '../../../../util/interfaces';

import {
    AwesomeIcon,
    capitalize,
    ChangeFunction,
    ChangeKeyFunction,
    ErrorType,
    Link,
    Loader,
    SelectItemShape
} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import Account from '../../../../models/accountModel';

import classNames from 'classnames';

import {AppContext} from '../../../misc/appContext';

import {BluemateButton, BluemateInput, BluemateSelect} from '../../../controls';

import CountryField from '../../../controls/country/field/countryField';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import BluemateSwitch from '../../../controls/switch/bluemateSwitch';

interface Props {
    onChange: ChangeKeyFunction;
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onClickAccount: (account: Account) => MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onClickLock: MouseEventHandler;
    onResetError: (key: keyof CommonAdminErrors) => FocusEventHandler;
    onChangeMode: (mode: DataMode) => MouseEventHandler;
    onChangeSubscriptionStatus: ChangeFunction<SubscriptionStatus>;
    onResetTwoFactor: MouseEventHandler;
    onChangeOverride: ChangeFunction<boolean>;
    onGenerateVerification: MouseEventHandler;
    onChangeAdminFlex: ChangeKeyFunction;
    errors: CommonAdminErrors;
    mode: DataMode;
    user: User;
    loading: boolean;
    accounts: Account[];
}

export default function UserDetails(props: Props) {
    const {t} = useTranslation();
    const subscriptionItems: SelectItemShape[] = [
        {label: t('admin.users.details.sub.active', 'Actief'), value: SubscriptionStatus.ACTIVE},
        {label: t('admin.users.details.sub.canceled', 'Geannuleerd'), value: SubscriptionStatus.CANCELED},
        {label: t('admin.users.details.sub.free', 'Gratis'), value: SubscriptionStatus.FREE},
        {label: t('admin.users.details.sub.inactive', 'Inactief'), value: SubscriptionStatus.INACTIVE},
        {label: t('admin.users.details.sub.unpaid', 'Onbetaald'), value: SubscriptionStatus.UNPAID},
        {label: t('admin.users.details.sub.trial', 'Proefperiode'), value: SubscriptionStatus.TRIAL}
    ];
    const {
        user,
        errors,
        mode,
        onChangeMode,
        loading,
        onChange,
        onResetError,
        onClickDelete,
        onClickLock,
        onClickAccount,
        onClose,
        onSubmit,
        onChangeSubscriptionStatus,
        accounts,
        onResetTwoFactor,
        onChangeOverride,
        onGenerateVerification,
        onChangeAdminFlex
    } = props;
    const context = useContext(AppContext);
    const userIsAdmin = context.userContext.user?.isAdmin() ?? false;

    const groupItems = useMemo(() => {
        const items: SelectItemShape[] = [
            {label: t('admin.users.details.type.user', 'Gebruiker'), value: UserType.USER},
            {label: t('admin.users.details.type.support', 'Support'), value: UserType.SUPPORT},
            {label: t('admin.users.details.type.premium', 'Premium gebruiker'), value: UserType.PREMIUM},
            {label: t('admin.users.details.type.admin', 'Admin'), value: UserType.ADMIN}
        ];
        if (context.userContext.user?.type === UserType.SUPER_ADMIN)
            items.push({label: t('admin.users.details.type.superAdmin', 'Super admin'), value: UserType.SUPER_ADMIN});
        return items;
    }, [context.userContext.user?.type, t]);

    const lockIcon = useMemo(() => {
        let iconName: IconName = 'lock-open';
        if (user.status === UserStatus.ACTIVE) iconName = 'lock';
        return <AwesomeIcon name={iconName} />;
    }, [user]);

    const lockLabel = useMemo(() => {
        return user.status === UserStatus.ACTIVE
            ? t('admin.users.details.block', 'Blokkeren')
            : t('admin.users.details.unblock', 'Deblokkeren');
    }, [user, t]);

    function renderHeader() {
        return (
            <div className='header-container'>
                <div className='header-items-container'>
                    <BluemateButton
                        onClick={onClose}
                        disabled={loading}
                        label={t('admin.users.details.back', 'Terug')}
                        ornaments={{element: <AwesomeIcon name='chevron-left' />}}
                    />
                    <label className='header-title'>{t('admin.users.details.title', 'Gebruiker details')}</label>
                </div>
                <div className='header-items-container'>
                    {userIsAdmin ? (
                        <>
                            <BluemateButton
                                onClick={onGenerateVerification}
                                label={t('admin.users.details.generateAccessCode', 'Toegangscode genereren')}
                                ornaments={{element: <AwesomeIcon name='fingerprint' />}}
                            />
                            <BluemateButton onClick={onClickLock} label={lockLabel} ornaments={{element: lockIcon}} />
                            <BluemateButton
                                onClick={onClickDelete}
                                label={t('admin.users.details.delete', 'Verwijderen')}
                                color={'red'}
                                secondary
                                ornaments={{element: <AwesomeIcon name='trash-alt' />}}
                            />
                        </>
                    ) : (
                        <div />
                    )}
                </div>
            </div>
        );
    }

    function renderBody() {
        return (
            <div className='body-container'>
                {userIsAdmin &&
                    (mode === DataMode.DETAILS ? (
                        <BluemateButton
                            onClick={onChangeMode(DataMode.EDIT)}
                            label={t('admin.users.details.edit', 'Wijzigen')}
                        />
                    ) : (
                        <BluemateButton
                            onClick={onChangeMode(DataMode.DETAILS)}
                            label={t('admin.users.details.cancel', 'Annuleren')}
                        />
                    ))}
                <div className='fields-container'>
                    <Loader active={loading} />
                    {renderGroupField()}
                    <div className='detail-field'>
                        <span className={classNames('key', mode === DataMode.EDIT && 'edit')}>
                            {t('admin.users.details.status', 'Status')}
                        </span>
                        <span className='value'>{user.translatedStatus}</span>
                    </div>
                    {renderField(
                        'firstname',
                        t('admin.users.details.firstname', 'Voornaam'),
                        user.firstname,
                        errors.firstname,
                        'firstname'
                    )}
                    {renderField(
                        'lastname',
                        t('admin.users.details.lastname', 'Achternaam'),
                        user.lastname,
                        errors.lastname,
                        'lastname'
                    )}
                    {renderField(
                        'address',
                        t('admin.users.details.address', 'Adres'),
                        user.address,
                        errors.address,
                        'address'
                    )}
                    {renderField(
                        'zip_code',
                        t('admin.users.details.zipCode', 'Postcode'),
                        user.zipCode,
                        errors.zipCode,
                        'zipCode'
                    )}
                    {renderField('city', t('admin.users.details.city', 'Stad'), user.city, errors.city, 'city')}
                    {renderCountryField(user.country)}
                    {renderField(
                        'email',
                        t('admin.users.details.email', 'E-mailadres'),
                        user.email,
                        errors.email,
                        'email'
                    )}
                    {renderField(
                        'tel_number',
                        t('admin.users.details.telNumber', 'Telefoonnummer'),
                        user.telNumber,
                        errors.telNumber,
                        'telNumber'
                    )}
                    <div className='detail-field col-3'>
                        <span className={classNames('key', mode === DataMode.EDIT && 'edit')}>
                            {t('admin.users.details.twoFactor', 'Tweestapsverificatie')}
                        </span>
                        <span className='value'>
                            {user.twoFactorEnabled
                                ? t('admin.users.details.on', 'Aan')
                                : t('admin.users.details.off', 'Uit')}
                        </span>
                        {user.twoFactorEnabled && (
                            <Link onClick={onResetTwoFactor}>
                                {t('admin.users.details.resetTwoFactor', 'Resetten')}
                            </Link>
                        )}
                    </div>
                    {renderSubscriptionStatus()}
                    {renderTrial()}
                    {renderOverride()}
                </div>
                {renderAccounts()}
            </div>
        );
    }

    function renderGroupField() {
        if (mode === DataMode.DETAILS)
            return (
                <div className='detail-field'>
                    <span className='key'>{t('admin.users.details.typeLabel', 'Type')}</span>
                    <span className='value'>{user.translatedType}</span>
                </div>
            );
        else
            return (
                <BluemateSelect
                    value={user.type}
                    items={groupItems}
                    onChange={onChange('type')}
                    label={t('admin.users.details.typeLabel', 'Type')}
                    className='edit-field'
                />
            );
    }

    function renderField(
        key: string,
        label: string,
        value: any,
        error?: ErrorType,
        errorKey?: keyof CommonAdminErrors
    ) {
        if (mode === DataMode.DETAILS)
            return (
                <div className='detail-field'>
                    <span className='key'>{label}</span>
                    <span className='value'>{value}</span>
                </div>
            );
        else {
            const onFocus = errorKey ? onResetError(errorKey) : undefined;
            return (
                <BluemateInput
                    value={value}
                    onChange={onChange(key)}
                    label={label}
                    error={error}
                    onFocus={onFocus}
                    disabled={loading}
                    className='edit-field'
                />
            );
        }
    }

    function renderCountryField(country: Country) {
        if (mode === DataMode.DETAILS)
            return (
                <div className='detail-field'>
                    <span className='key'>{t('admin.users.details.country', 'Land')}</span>
                    <span className='value'>{country}</span>
                </div>
            );
        return <CountryField onChange={onChange('country')} value={country} labelAsValue />;
    }

    function renderSubscriptionStatus() {
        if (mode === DataMode.DETAILS)
            return (
                <>
                    <div className='detail-field'>
                        <span className='key'>{t('admin.users.details.subscription', 'Abonnement')}</span>
                        <span className='value'>
                            {user.subscription?.statusLabel ??
                                t('admin.users.details.noSubscription', 'Geen abonnement')}
                        </span>
                    </div>
                    <div className='detail-field'>
                        <span className='key'>{t('admin.users.details.stripeId', 'Stripe ID')}</span>
                        <span className='value'>{user.subscription?.customerId ?? '-'}</span>
                    </div>
                </>
            );
        return (
            <BluemateSelect
                value={user.subscription?.status ?? SubscriptionStatus.INACTIVE}
                onChange={onChangeSubscriptionStatus}
                items={subscriptionItems}
                label={t('admin.users.details.subStatus', 'Status abonnement')}
            />
        );
    }

    function renderTrial() {
        const trialEnd = user.subscription?.trialEndDate;
        if (!trialEnd) return null;
        const days = trialEnd.endOf('day').diff(IDate.now(), 'days');
        const daysText =
            days >= 0
                ? ` (${days} ${days === 1 ? t('admin.users.details.day', 'dag') : t('admin.users.details.days', 'dagen')})`
                : null;
        return (
            <div className='detail-field'>
                <span className={classNames('key', mode === DataMode.EDIT && 'edit')}>
                    {t('admin.users.details.trialUntil', 'Proefperiode t\\m')}
                </span>
                <span className='value'>
                    {trialEnd.format('DD MMMM')}
                    {daysText}
                </span>
            </div>
        );
    }

    function renderOverride() {
        if (mode === DataMode.DETAILS)
            return (
                <>
                    <div className='detail-field'>
                        <span className='key'>{t('admin.users.details.overrideLimit', 'Negeer beperkingen')}</span>
                        <span className='value'>
                            {user.overrideLimitations
                                ? t('admin.users.details.yes', 'Ja')
                                : t('admin.users.details.no', 'Nee')}
                        </span>
                    </div>
                    <div className='detail-field'>
                        <span className='key'>
                            {t('admin.users.details.overrideEmail', 'Negeer e-mail beleidspunten')}
                        </span>
                        <span className='value'>
                            {user.getAdminFlex('override_email_rules')
                                ? t('admin.users.details.yes', 'Ja')
                                : t('admin.users.details.no', 'Nee')}
                        </span>
                    </div>
                </>
            );
        return (
            <>
                <BluemateSwitch
                    value={user.overrideLimitations}
                    onChange={onChangeOverride}
                    mainLabel={t('admin.users.details.overrideLimit', 'Negeer beperkingen')}
                    activeRed
                />
                <BluemateSwitch
                    value={user.getAdminFlex('override_email_rules')}
                    onChange={onChangeAdminFlex('override_email_rules')}
                    mainLabel={t('admin.users.details.overrideEmail', 'Negeer e-mail beleidspunten')}
                    activeRed
                />
            </>
        );
    }

    function renderAccounts() {
        return (
            <div className='accounts-container'>
                <label className='accounts-title'>{t('admin.users.details.accounts', 'Accounts')}</label>
                <div className='accounts'>
                    <div className='row head'>
                        <label>{t('admin.users.details.col.createdAt', 'AANGEMAAKT OP')}</label>
                        <label>{t('admin.users.details.col.companyName', 'BEDRIJFSNAAM')}</label>
                        <label>{t('admin.users.details.col.status', 'STATUS')}</label>
                    </div>
                    {accounts.map(renderAccount)}
                </div>
            </div>
        );
    }

    function renderAccount(account: Account) {
        return (
            <div className='row' onClick={onClickAccount(account)} key={account.id}>
                <label>{account.createdAtMoment.format('DD-MM-YYYY HH:mm')}</label>
                <label>{account.selectablePlatformLabel}</label>
                <label>{capitalize(account.translatedStatus)}</label>
            </div>
        );
    }

    function renderFooter() {
        return (
            <div className='footer-container'>
                <BluemateButton onClick={onClose} label={t('admin.users.details.close', 'Sluiten')} />
                {userIsAdmin ? (
                    <BluemateButton onClick={onSubmit} label={t('admin.users.details.save', 'Opslaan')} />
                ) : (
                    <div />
                )}
            </div>
        );
    }

    return (
        <div className='user-details'>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    );
}
